/**
 * TargetGroupFilter plugin.
 *
 * Filters grant calls by target group (e.g., SME, SELF_EMPLOYED).
 * URL param: targetGroup (singular), API/SavedView: targetGroups (plural).
 *
 * Uses faceted search counts from parent (targetGroupCounts).
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 4)
 */

import React, { useMemo } from "react";
import { FilterPlugin, FilterComponentProps } from "../types";
import { SavedViewFilters } from "../../../../../services/grantsApi";
import MultiSelectDropdown from "../../../../common/MultiSelectDropdown";
import { TargetGroupCount } from "../../../../../types/grants";

/**
 * Target group filter React component.
 * Uses faceted search counts from parent via props.facets.
 */
const TargetGroupFilterComponent = React.memo(
  function TargetGroupFilterComponent(props: FilterComponentProps<string[]>) {
    const { value, onChange, facets } = props;

    const options = useMemo(
      () =>
        ((facets?.targetGroupCounts as TargetGroupCount[]) ?? []).map(
          (tg) => ({
            value: tg.targetGroup,
            label: tg.label,
            count: tg.count,
          })
        ),
      [facets?.targetGroupCounts]
    );

    return React.createElement("div", null,
      React.createElement("label", {
        className: "block text-sm font-medium text-neutral-700 mb-1",
      }, "Grupa docelowa"),
      React.createElement(MultiSelectDropdown, {
        options,
        selectedValues: value,
        onChange,
        placeholder: "Wszystkie grupy docelowe",
        ariaLabel: "Filtruj według grupy docelowej",
      }),
      React.createElement("p", {
        className: "mt-1 text-xs text-neutral-500",
      }, "Wyświetla nabory pasujące do dowolnej z wybranych grup")
    );
  }
);

export const TargetGroupFilterPlugin: FilterPlugin<string[]> = {
  key: "targetGroup",
  label: "Grupa docelowa",
  placement: "advanced",
  order: 3,
  defaultValue: [],
  Component: TargetGroupFilterComponent,

  isActive: (value: string[]): boolean => value.length > 0,

  toUrlParams: (value: string[], params: URLSearchParams): void => {
    value.forEach((tg) => params.append("targetGroup", tg));
  },

  fromUrlParams: (params: URLSearchParams): string[] => {
    return params.getAll("targetGroup");
  },

  toSavedView: (
    value: string[],
    filters: Partial<SavedViewFilters>
  ): void => {
    filters.targetGroups = value;
  },

  fromSavedView: (filters: SavedViewFilters): string[] => {
    return filters.targetGroups ?? [];
  },

  toApiParams: (value: string[], params: URLSearchParams): void => {
    value.forEach((tg) => params.append("targetGroups", tg));
  },
};
